import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  HostListener,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  ToggleCardComponent,
  ToggleCardData,
} from '../shared/components/toggle-card/toggle-card.component';
import {
  BrandingPanelComponent,
  BrandingData,
} from './components/branding-panel/branding-panel.component';
import { TemplatesPanelComponent } from './components/templates-panel/templates-panel.component';
import { TemplateData } from '../shared/components/template-card/template-card.component';
import { ReplaceTemplateModalComponent } from './components/replace-template-modal/replace-template-modal.component';
// Shared preview themes from @way/website-themes (editor canvas + public preview)
import {
  HomePagePreviewComponent,
  WebsiteElement,
} from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { ExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/minimal_website/express-wash-packages-preview/express-wash-packages-preview.component';
import { ExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/minimal_website/express-auto-details-preview/express-auto-details-preview.component';
import { FleetManagementPreviewComponent } from '@way/website-themes/themes/minimal_website/fleet-management-preview/fleet-management-preview.component';
import { CouponsPreviewComponent } from '@way/website-themes/themes/minimal_website/coupons-preview/coupons-preview.component';
import { DarkHomePagePreviewComponent } from '@way/website-themes/themes/dark_theme/home-page-preview/home-page-preview.component';
import { DarkExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/dark_theme/express-wash-packages-preview/express-wash-packages-preview.component';
import { DarkExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/dark_theme/express-auto-details-preview/express-auto-details-preview.component';
import { DarkFleetManagementPreviewComponent } from '@way/website-themes/themes/dark_theme/fleet-management-preview/fleet-management-preview.component';
import { DarkCouponsPreviewComponent } from '@way/website-themes/themes/dark_theme/coupons-preview/coupons-preview.component';
import { ModernHomePagePreviewComponent } from '@way/website-themes/themes/modern_theme/home-page-preview/home-page-preview.component';
import { ModernExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/modern_theme/express-wash-packages-preview/express-wash-packages-preview.component';
import { ModernExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/modern_theme/express-auto-details-preview/express-auto-details-preview.component';
import { ModernFleetManagementPreviewComponent } from '@way/website-themes/themes/modern_theme/fleet-management-preview/fleet-management-preview.component';
import { ModernCouponsPreviewComponent } from '@way/website-themes/themes/modern_theme/coupons-preview/coupons-preview.component';
import { ChampsHomePagePreviewComponent } from '@way/website-themes/themes/champs_theme/home-page-preview/home-page-preview.component';
import { ChampsExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/champs_theme/express-wash-packages-preview/express-wash-packages-preview.component';
import { ChampsExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/champs_theme/express-auto-details-preview/express-auto-details-preview.component';
import { ChampsMembershipsPreviewComponent } from '@way/website-themes/themes/champs_theme/memberships-preview/memberships-preview.component';
import { ChampsFleetManagementPreviewComponent } from '@way/website-themes/themes/champs_theme/fleet-management-preview/fleet-management-preview.component';
import { ChampsCouponsPreviewComponent } from '@way/website-themes/themes/champs_theme/coupons-preview/coupons-preview.component';
import { ToolbarAction } from '../shared/components/toolbar';
import { DeviceType } from '../shared/components/device-selector';
import { TemplateService } from '../car-wash-website/services/template.service';
import { HeaderPanelComponent, HeaderData } from './components/header-panel/header-panel.component';
import { FooterPanelComponent, FooterData } from './components/footer-panel/footer-panel.component';
import { WebsiteSettingsComponent } from './components/website-settings/website-settings.component';
import { UndoRedoService, StateSnapshot } from './services/undo-redo.service';
import { LoggerService } from '../services/logger.service';
import { BuilderContext } from './services/builder-context';
import { TemplatePopulatorService } from './services/template-populator.service';
import { BuilderAutosaveService } from './services/builder-autosave.service';
import { SectionPanelsService, SectionContext } from './services/section-panels.service';
import { HeroPanelService, HeroContext } from './services/hero-panel.service';
import { InfoContentService, InfoContext } from './services/info-content.service';
import { BuilderDataService, DataContext } from './services/builder-data.service';
import { BrandingManagerService, BrandingContext } from './services/branding-manager.service';
import { ElementSelectionService, ElementContext } from './services/element-selection.service';

// Shared Panels (used by all templates)
import {
  MinimalHeroPanelComponent,
  HeroPanelData as MinimalHeroPanelData,
} from './components/template/panels/hero-panel';
import {
  MinimalInfoPanelComponent,
  InfoPanelData as MinimalInfoPanelData,
} from './components/template/panels/info-panel';
import {
  WashPackagesPanelComponent,
  WashPackagePanelData,
} from './components/template/panels/wash-packages-panel';
import {
  ExpressDetailsPanelComponent,
  ExpressDetailsPanelData,
} from './components/template/panels/express-details-panel';
import {
  CouponsPanelComponent,
  CouponsPanelData,
} from './components/template/panels/coupons-panel';
import { FleetsPanelComponent, FleetsPanelData } from './components/template/panels/fleets-panel';
import {
  MinimalTestimonialsPanelComponent,
  TestimonialsData as MinimalTestimonialsData,
} from './components/template/panels/testimonials-panel';
import {
  MinimalBookingPanelComponent,
  BookingData as MinimalBookingData,
} from './components/template/panels/booking-panel';
import {
  MinimalContactPanelComponent,
  ContactData as MinimalContactData,
} from './components/template/panels/contact-panel';
import { getBaseWebsiteUrl } from '../shared/utils/website-url.util';

// Use unified types - use Minimal panel data types which have all layouts
export type HeroPanelData = MinimalHeroPanelData;
export type InfoPanelData = MinimalInfoPanelData;
export type TestimonialsData = MinimalTestimonialsData;
export type BookingData = MinimalBookingData;
export type ContactData = MinimalContactData;

@Component({
  selector: 'app-website-builder',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    FormsModule,
    MatTooltipModule,
    MatSlideToggleModule,
    ToggleCardComponent,
    BrandingPanelComponent,
    TemplatesPanelComponent,
    HomePagePreviewComponent,
    ExpressWashPackagesPreviewComponent,
    ExpressAutoDetailsPreviewComponent,
    FleetManagementPreviewComponent,
    CouponsPreviewComponent,
    DarkHomePagePreviewComponent,
    DarkExpressWashPackagesPreviewComponent,
    DarkExpressAutoDetailsPreviewComponent,
    DarkFleetManagementPreviewComponent,
    DarkCouponsPreviewComponent,
    ModernHomePagePreviewComponent,
    ModernExpressWashPackagesPreviewComponent,
    ModernExpressAutoDetailsPreviewComponent,
    ModernFleetManagementPreviewComponent,
    ModernCouponsPreviewComponent,
    // Champs Theme Template components
    ChampsHomePagePreviewComponent,
    ChampsExpressWashPackagesPreviewComponent,
    ChampsExpressAutoDetailsPreviewComponent,
    ChampsMembershipsPreviewComponent,
    ChampsFleetManagementPreviewComponent,
    ChampsCouponsPreviewComponent,
    // Shared Panels (used by all templates)
    MinimalHeroPanelComponent,
    MinimalInfoPanelComponent,
    MinimalTestimonialsPanelComponent,
    MinimalBookingPanelComponent,
    MinimalContactPanelComponent,
    WashPackagesPanelComponent,
    ExpressDetailsPanelComponent,
    CouponsPanelComponent,
    FleetsPanelComponent,
    // Shared Panels
    HeaderPanelComponent,
    FooterPanelComponent,
    WebsiteSettingsComponent,
    // Modals
    ReplaceTemplateModalComponent,
  ],
  templateUrl: './website-builder.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./website-builder.component.scss'],
})
export class WebsiteBuilderComponent implements OnInit, OnDestroy {
  // Template/Website IDs from URL
  templateId: string | null = null;
  websiteId: string | null = null;
  templatePageId: string | null = null;
  websitePageId: string | null = null; // ID of the website page (from website_pages)
  private website: any = null; // Store the full website object from API for accessing json_data.website.footer
  currentTemplateId: string | null = null; // Current template ID to determine which template to use
  // Single source of truth for current page JSON
  currentJsonData: any = null;
  // If true, editor was opened with only websiteId and should bypass initial page create
  openedFromExisting = false;
  templateData: any = null;
  primaryColor: string | null = null; // Primary color from theme
  headingColor: string | null = null; // Heading color from branding
  paragraphColor: string | null = null; // Paragraph/description color from branding
  headingFont: string | null = null; // Heading font from branding
  bodyFont: string | null = null; // Body font from branding
  loading = false;
  error: string | null = null;
  // Autosave — state owned by BuilderAutosaveService; legacy null guard kept for ngOnDestroy
  private autosaveDebounce: any = null;

  // Subscription management
  private routeSubscription?: Subscription;

  // Device view states
  currentDevice: DeviceType = 'desktop';

  // Website preview data
  websiteData = {
    title: 'Homepage',
    dimensions: '1440x1920',
    lastSaved: 'Just now',
  };

  // Panel states
  activeSidebarItem: 'pages' | 'branding' | 'templates' = 'pages';
  showPagesPanel = false;
  showBrandingPanel = false;
  showTemplatesPanel = false;
  showSettingsPage = false;
  initialSettingsSidebarItem: 'general' | 'domains' | null = null;
  showHeroPanel = false;
  showInfoPanel = false;
  showTestimonialsPanel = false;
  showBookingPanel = false;
  showContactPanel = false;
  showHeaderPanel = false;
  showWashPackagesPanel = false;
  showExpressDetailsPanel = false;
  showCouponsPanel = false;
  showFleetsPanel = false;
  showFooterPanel = false;
  activeTab: 'pages' | 'sections' = 'pages';

  // Track which info section variant is currently being edited
  currentInfoSectionKey = 'info_section';

  // Templates data
  templates: any[] = [];
  templatesLoading = false;
  templatesError: string | null = null;

  // Replace template modal state
  showReplaceTemplateModal = false;
  pendingTemplate: TemplateData | null = null;
  isReplacingTemplate = false;

  isPublishToggling = false;
  isPublishStatusLoading = false;
  websitePublishStatus = false;
  websiteUnpublishStatus = false;

  // Testimonials data
  testimonialsData: TestimonialsData = {
    title: '',
    subtitle: '',
    testimonials: [],
    cardStyle: 'default',
    backgroundColor: '#FFFFFF',
  };

  // Booking data
  bookingData: BookingData = {
    title: '',
    subtitle: '',
    formTheme: {
      labelColor: '#555963',
      inputBorderColor: '#E9EAEC',
      inputBackgroundColor: '#FFFFFF',
      inputTextColor: '#15172E',
    },
    button: {
      text: 'Book Appointment',
      backgroundColor: '#1B5BFF',
      textColor: '#FFFFFF',
    },
    backgroundColor: '#FFFFFF',
  };

  // Contact data
  contactData: ContactData = {
    title: '',
    subtitle: '',
    map: {
      imageUrl: '',
      coordinates: { lat: 0, lng: 0 },
    },
    contactInfo: {
      phone: '',
      email: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
      website: '',
    },
    socialLinks: [],
    businessHours: [],
    backgroundColor: '#FFFFFF',
  };

  // Header panel data
  headerData: HeaderData = {
    logo: {
      image: '',
      alt: 'Logo',
      text: 'Logo',
    },
    navigation: [],
    ctaButton: {
      text: 'Book Wash',
      link: '#express-wash-packages',
      buttonNavigation: 'express-washes',
      backgroundColor: '#1B5BFF',
      textColor: '#FFFFFF',
    },
    backgroundColor: '#F8F9FA',
  };

  // Footer panel data
  footerData: FooterData = {
    logo: {
      text: 'way',
    },
    quickLinks: [],
    quickLinksTitle: 'Quick links',
    socialLinks: [
      { platform: 'Instagram', url: '#', icon: 'instagram' },
      { platform: 'Facebook', url: '#', icon: 'facebook' },
      { platform: 'YouTube', url: '#', icon: 'youtube' },
      { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
      { platform: 'X', url: '#', icon: 'x' },
    ],
    socialLinksTitle: 'Connect with us',
    copyright: `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`,
    backgroundColor: '#000920',
  };

  // Toolbar configuration
  toolbarActions: ToolbarAction[][] = [
    [
      {
        id: 'undo',
        icon: 'assets/images/website-builder/arrow-back-up.svg',
        label: 'Undo',
        disabled: false,
      },
      {
        id: 'redo',
        icon: 'assets/images/website-builder/arrow-back-up-redo.svg',
        label: 'Redo',
        disabled: true,
      },
    ],
    [
      { id: 'refresh', icon: 'assets/images/website-builder/refresh-alert.svg', label: 'Refresh' },
      { id: 'maximize', icon: 'assets/images/website-builder/maximize.svg', label: 'Maximize' },
    ],
    [
      { id: 'zoom-out', icon: 'assets/images/website-builder/zoom-out.svg', label: 'Zoom Out' },
      { id: 'zoom-in', icon: 'assets/images/website-builder/zoom-in.svg', label: 'Zoom In' },
    ],
  ];

  // Pages data (runtime-driven)
  pages: ToggleCardData[] = [];

  // Sections data (runtime-driven)
  sections: (ToggleCardData & { jsonData?: any })[] = [];

  // Cached visibility map and pages for navigation (performance optimization)
  private _sectionVisibilityMap: Record<string, boolean> = {};
  private _sectionVisibilityMapCacheKey = '';
  private _pagesForNavigation: any[] = [];
  private _pagesForNavigationCacheKey = '';

  // Track current page type
  currentPageType = 'home';

  // Branding data
  brandingData: BrandingData = {
    colors: {
      primary: '',
      heading: '',
      paragraph: '',
    },
    fonts: {
      heading: '',
      body: '',
    },
  };

  // Website elements data
  websiteElements: WebsiteElement[] = [];

  // Hero Panel
  heroPanelData: HeroPanelData = {
    heading: '',
    paragraph: '',
    image: {
      src: '',
      alt: '',
      width: '420px',
      height: '320px',
      scaletype: 'fit',
    },
    // Backward compatibility
    imageUrl: '',
    imageFit: 'fit',
    buttonText: '',
    buttonNavigation: 'homepage',
    backgroundColor: '',
    layout: '1',
  };

  infoPanelData: InfoPanelData = {
    title: '',
    description: '',
    imageUrl: '',
    imageFit: 'fill',
    buttonText: '',
    buttonNavigation: 'homepage',
    backgroundColor: '',
    layout: '1',
  };

  washPackagesPanelData: WashPackagePanelData = {
    packages: [],
  };
  expressDetailsPanelData: ExpressDetailsPanelData = {
    packages: [],
  };
  couponsPanelData: CouponsPanelData = {
    title: '',
    subtitle: '',
    promoCodeLabel: 'PROMO CODE',
    promoCode: '',
    availabilityLabel: 'Available for the following washes',
    washPackages: [],
    instructionHeading: '',
    instructionSteps: [],
  };
  fleetsPanelData: FleetsPanelData = {
    title: '',
    subtitle: '',
    formTheme: 'light',
    buttonText: 'Submit',
    backgroundColor: '#FFFFFF',
  };

  // Tool states
  private readonly DEFAULT_ZOOM_LEVEL = 40; // Original/default zoom level
  zoomLevel = 40; // Desktop preview scaled down 20% more (50% - 20% = 30%)
  canUndo = false;
  canRedo = false;

  // Undo/Redo subscriptions
  private undoRedoSubscriptions: Subscription[] = [];

  // Icon paths - centralized for reusability
  readonly ICONS = {
    pages: 'assets/images/website-builder/pages-icon.svg',
    branding: 'assets/images/website-builder/branding-icon.svg',
    templates: 'assets/images/website-builder/templates-icon.svg',
    logout: 'assets/images/website-builder/logout-icon.svg',
    settings: 'assets/images/website-builder/settings.svg',
    preview: 'assets/images/website-builder/player-play.svg',
    desktop: 'assets/images/website-builder/device-desktop.svg',
    tablet: 'assets/images/website-builder/device-tablet.svg',
    mobile: 'assets/images/website-builder/device-mobile.svg',
    undo: 'assets/images/website-builder/arrow-back-up.svg',
    redo: 'assets/images/website-builder/arrow-back-up-redo.svg',
    refresh: 'assets/images/website-builder/refresh-alert.svg',
    maximize: 'assets/images/website-builder/maximize.svg',
    zoomIn: 'assets/images/website-builder/zoom-in.svg',
    zoomOut: 'assets/images/website-builder/zoom-out.svg',
  };

  constructor(
    private route: ActivatedRoute,
    private templateService: TemplateService,
    private undoRedoService: UndoRedoService,
    private logger: LoggerService,
    private templatePopulator: TemplatePopulatorService,
    private autosaveService: BuilderAutosaveService,
    private sectionPanels: SectionPanelsService,
    private heroPanelService: HeroPanelService,
    private infoContentService: InfoContentService,
    private builderData: BuilderDataService,
    private brandingManager: BrandingManagerService,
    private elementSelection: ElementSelectionService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /** Build a BuilderContext snapshot from the current component state. */
  private getContext(): BuilderContext {
    return {
      websiteId: this.websiteId,
      websitePageId: this.websitePageId,
      currentPageType: this.currentPageType,
      templateId: this.templateId,
    };
  }

  /** Build a SectionContext that gives section-panel service methods access to component state. */
  private getSectionContext(): SectionContext {
    return {
      json: this.currentJsonData,
      templateData: this.templateData,
      elements: this.websiteElements,
      washPackagesPanelData: this.washPackagesPanelData,
      expressDetailsPanelData: this.expressDetailsPanelData,
      couponsPanelData: this.couponsPanelData,
      fleetsPanelData: this.fleetsPanelData,
      showWashPackagesPanel: this.showWashPackagesPanel,
      showExpressDetailsPanel: this.showExpressDetailsPanel,
      showCouponsPanel: this.showCouponsPanel,
      showFleetsPanel: this.showFleetsPanel,
      scheduleAutosave: paths => this.scheduleAutosave(paths),
    };
  }

  /** Apply any fields mutated by a SectionContext back to the component. */
  private applySectionContext(ctx: SectionContext): void {
    this.currentJsonData = ctx.json;
    this.websiteElements = ctx.elements;
    this.washPackagesPanelData = ctx.washPackagesPanelData;
    this.expressDetailsPanelData = ctx.expressDetailsPanelData;
    this.couponsPanelData = ctx.couponsPanelData;
    this.fleetsPanelData = ctx.fleetsPanelData;
    this.showWashPackagesPanel = ctx.showWashPackagesPanel;
    this.showExpressDetailsPanel = ctx.showExpressDetailsPanel;
    this.showCouponsPanel = ctx.showCouponsPanel;
    this.showFleetsPanel = ctx.showFleetsPanel;
  }

  /** Build a HeroContext from the current component state. */
  private getHeroContext(): HeroContext {
    return {
      json: this.currentJsonData,
      elements: this.websiteElements,
      heroPanelData: this.heroPanelData,
      templateData: this.templateData,
      showHeroPanel: this.showHeroPanel,
      scheduleAutosave: paths => this.scheduleAutosave(paths),
      flushAutosave: () => this.flushAutosave(),
      recordStateChange: desc => this.recordStateChange(desc),
    };
  }

  /** Apply hero-context mutations back to the component. */
  private applyHeroContext(ctx: HeroContext): void {
    this.currentJsonData = ctx.json;
    this.websiteElements = ctx.elements;
    this.heroPanelData = ctx.heroPanelData;
  }

  /** Build an InfoContext from the current component state. */
  private getInfoContext(): InfoContext {
    return {
      json: this.currentJsonData,
      elements: this.websiteElements,
      templateData: this.templateData,
      infoPanelData: this.infoPanelData,
      testimonialsPanelData: this.testimonialsData,
      bookingPanelData: this.bookingData,
      contactPanelData: this.contactData,
      footerData: this.footerData,
      headerData: this.headerData,
      brandingData: this.brandingData,
      website: this.website,
      pages: this.pages,
      currentPageType: this.currentPageType,
      showInfoPanel: this.showInfoPanel,
      showTestimonialsPanel: this.showTestimonialsPanel,
      showBookingPanel: this.showBookingPanel,
      showFooterPanel: this.showFooterPanel,
      showHeaderPanel: this.showHeaderPanel,
      showContactPanel: this.showContactPanel,
      currentInfoSectionKey: this.currentInfoSectionKey,
      scheduleAutosave: paths => this.scheduleAutosave(paths),
      recordStateChange: desc => this.recordStateChange(desc),
    };
  }

  /** Apply info-context mutations back to the component. */
  private applyInfoContext(ctx: InfoContext): void {
    this.currentJsonData = ctx.json
      ? {
          ...ctx.json,
          page: ctx.json.page
            ? {
                ...ctx.json.page,
                sections: ctx.json.page.sections
                  ? Object.fromEntries(
                      Object.entries(ctx.json.page.sections).map(
                        ([key, section]: [string, any]) => [
                          key,
                          {
                            ...section,
                            layout: section?.layout ? { ...section.layout } : section?.layout,
                          },
                        ]
                      )
                    )
                  : ctx.json.page.sections,
              }
            : ctx.json.page,
        }
      : ctx.json;
    this.websiteElements = [...ctx.elements];
    this.currentInfoSectionKey = ctx.currentInfoSectionKey;
    this.infoPanelData = ctx.infoPanelData;
    this.testimonialsData = ctx.testimonialsPanelData;
    this.bookingData = ctx.bookingPanelData;
    this.contactData = ctx.contactPanelData;
    this.footerData = ctx.footerData;
    this.headerData = ctx.headerData;
    this.showInfoPanel = ctx.showInfoPanel;
    this.showTestimonialsPanel = ctx.showTestimonialsPanel;
    this.showBookingPanel = ctx.showBookingPanel;
    this.showContactPanel = ctx.showContactPanel;
    this.showFooterPanel = ctx.showFooterPanel;
    this.showHeaderPanel = ctx.showHeaderPanel;
  }

  /** Copy ctx fields into component state before populate/build operations. */
  private syncDataCtxToComponent(ctx: DataContext): void {
    this.website = ctx.website;
    this.currentJsonData = ctx.json;
    this.templateData = ctx.templateData;
    this.pages = ctx.pages;
    this.currentPageType = ctx.currentPageType;
    this.brandingData = ctx.brandingData;
    this.primaryColor = ctx.primaryColor;
    this.headingColor = ctx.headingColor;
    this.paragraphColor = ctx.paragraphColor;
    this.headingFont = ctx.headingFont;
    this.bodyFont = ctx.bodyFont;
    this.heroPanelData = ctx.heroPanelData;
    this.footerData = ctx.footerData;
    this.headerData = ctx.headerData;
    if (ctx.elements?.length) {
      this.websiteElements = ctx.elements;
    }
  }

  /** Copy mutated component state back into ctx after populate/build operations. */
  private syncComponentToDataCtx(ctx: DataContext): void {
    ctx.elements = this.websiteElements;
    ctx.json = this.currentJsonData;
    ctx.heroPanelData = this.heroPanelData;
    ctx.footerData = this.footerData;
    ctx.headerData = this.headerData;
  }

  /** Run a data operation with ctx→component sync before and component→ctx sync after. */
  private runDataCtxOp(ctx: DataContext, op: () => void): void {
    this.syncDataCtxToComponent(ctx);
    op();
    this.syncComponentToDataCtx(ctx);
  }

  /** Build a DataContext from the current component state. */
  private getDataContext(): DataContext {
    const ctx: DataContext = {
      websiteId: this.websiteId,
      websitePageId: this.websitePageId,
      templateId: this.templateId,
      templatePageId: this.templatePageId,
      currentTemplateId: this.currentTemplateId,
      loading: this.loading,
      error: this.error,
      website: this.website,
      websiteData: this.websiteData,
      pages: this.pages,
      sections: this.sections,
      templateData: this.templateData,
      templates: this.templates,
      currentPageType: this.currentPageType,
      json: this.currentJsonData,
      elements: this.websiteElements,
      heroPanelData: this.heroPanelData,
      footerData: this.footerData,
      headerData: this.headerData,
      brandingData: this.brandingData,
      primaryColor: this.primaryColor,
      headingColor: this.headingColor,
      paragraphColor: this.paragraphColor,
      headingFont: this.headingFont,
      bodyFont: this.bodyFont,
      showHeroPanel: this.showHeroPanel,
      showBrandingPanel: this.showBrandingPanel,
      _pagesForNavigation: this._pagesForNavigation,
      _pagesForNavigationCacheKey: this._pagesForNavigationCacheKey,
      _sectionVisibilityMap: this._sectionVisibilityMap,
      _sectionVisibilityMapCacheKey: this._sectionVisibilityMapCacheKey,
      closeAllPanels: () => this.closeAllPanels(),
      updateTemplateSelection: id => this.updateTemplateSelection(id),
      initializeMasterState: d => this.initializeMasterState(d),
      recordStateChange: d => this.recordStateChange(d),
      scheduleAutosave: paths => this.scheduleAutosave(paths),
      generateHtmlFromJson: j => this.generateHtmlFromJson(j),
      cleanupJsonStructure: j => this.cleanupJsonStructure(j),
      populateHeroFromTemplate: () => undefined,
      populateSectionsFromTemplate: () => undefined,
      populateTestimonialsFromTemplate: () => undefined,
      populateBookingFromTemplate: () => undefined,
      populateContactFromTemplate: () => undefined,
      populateHeaderFromTemplate: () => undefined,
      populateFooterFromTemplate: () => undefined,
      populateFromJson: () => undefined,
      buildElementsFromJsonSections: () => undefined,
      ensureBaseElements: () => undefined,
    };

    ctx.populateHeroFromTemplate = () =>
      this.runDataCtxOp(ctx, () => this.populateHeroFromTemplate());
    ctx.populateSectionsFromTemplate = () =>
      this.runDataCtxOp(ctx, () => this.populateSectionsFromTemplate());
    ctx.populateTestimonialsFromTemplate = () =>
      this.runDataCtxOp(ctx, () => this.populateTestimonialsFromTemplate());
    ctx.populateBookingFromTemplate = () =>
      this.runDataCtxOp(ctx, () => this.populateBookingFromTemplate());
    ctx.populateContactFromTemplate = () =>
      this.runDataCtxOp(ctx, () => this.populateContactFromTemplate());
    ctx.populateHeaderFromTemplate = () =>
      this.runDataCtxOp(ctx, () => this.populateHeaderFromTemplate());
    ctx.populateFooterFromTemplate = () =>
      this.runDataCtxOp(ctx, () => this.populateFooterFromTemplate());
    ctx.populateFromJson = j => this.runDataCtxOp(ctx, () => this.populateFromJson(j));
    ctx.buildElementsFromJsonSections = () =>
      this.runDataCtxOp(ctx, () => this.buildElementsFromJsonSections());
    ctx.ensureBaseElements = () => this.runDataCtxOp(ctx, () => this.ensureBaseElements());

    return ctx;
  }

  /** Apply data-context mutations back to the component. */
  private applyDataContext(ctx: DataContext): void {
    this.websiteId = ctx.websiteId;
    this.websitePageId = ctx.websitePageId;
    this.templateId = ctx.templateId;
    this.templatePageId = ctx.templatePageId;
    this.loading = ctx.loading;
    this.error = ctx.error;
    this.website = ctx.website;
    this.websiteData = ctx.websiteData;
    this.pages = ctx.pages;
    this.sections = ctx.sections;
    this.templateData = ctx.templateData;
    this.templates = ctx.templates;
    this.currentPageType = ctx.currentPageType;
    this.currentTemplateId = ctx.currentTemplateId;
    this.currentJsonData = ctx.json;
    this.websiteElements = ctx.elements;
    this.heroPanelData = ctx.heroPanelData;
    this.footerData = ctx.footerData;
    this.headerData = ctx.headerData;
    this.brandingData = ctx.brandingData;
    this.primaryColor = ctx.primaryColor;
    this.headingColor = ctx.headingColor;
    this.paragraphColor = ctx.paragraphColor;
    this.headingFont = ctx.headingFont;
    this.bodyFont = ctx.bodyFont;
    this._pagesForNavigation = ctx._pagesForNavigation;
    this._pagesForNavigationCacheKey = ctx._pagesForNavigationCacheKey;
    this._sectionVisibilityMap = ctx._sectionVisibilityMap;
    this._sectionVisibilityMapCacheKey = ctx._sectionVisibilityMapCacheKey;
    this.syncPublishStateFromWebsite();
    if (this.websiteId && !ctx.loading) {
      this.isPublishStatusLoading = false;
    }
  }

  private syncPublishStateFromWebsite(): void {
    if (!this.website) {
      return;
    }
    if (this.website.publish !== undefined) {
      this.websitePublishStatus = !!this.website.publish;
    }
    if (this.website.unpublish !== undefined) {
      this.websiteUnpublishStatus = !!this.website.unpublish;
    }
  }

  private setPublishState(publish: boolean, unpublish: boolean): void {
    this.websitePublishStatus = publish;
    this.websiteUnpublishStatus = unpublish;
    if (this.website) {
      this.website = { ...this.website, publish, unpublish };
    }
  }

  /** Build a BrandingContext from current state. */
  private getBrandingContext(): BrandingContext {
    return {
      websiteId: this.websiteId,
      website: this.website,
      brandingData: this.brandingData,
      elements: this.websiteElements,
      json: this.currentJsonData,
      footerData: this.footerData,
      headerData: this.headerData,
      primaryColor: this.primaryColor,
      headingColor: this.headingColor,
      paragraphColor: this.paragraphColor,
      headingFont: this.headingFont,
      bodyFont: this.bodyFont,
      showBrandingPanel: this.showBrandingPanel,
      populateHeaderFromTemplate: () => this.populateHeaderFromTemplate(),
      populateFooterFromTemplate: () => this.populateFooterFromTemplate(),
      scheduleAutosave: paths => this.scheduleAutosave(paths),
    };
  }

  /** Apply branding-context mutations back to the component. */
  private applyBrandingContext(ctx: BrandingContext): void {
    this.brandingData = ctx.brandingData;
    this.websiteElements = ctx.elements;
    this.currentJsonData = ctx.json;
    this.footerData = ctx.footerData;
    this.headerData = ctx.headerData;
    this.primaryColor = ctx.primaryColor;
    this.headingColor = ctx.headingColor;
    this.paragraphColor = ctx.paragraphColor;
    this.headingFont = ctx.headingFont;
    this.bodyFont = ctx.bodyFont;
  }

  /** Build an ElementContext from current state. */
  private getElementContext(): ElementContext {
    return {
      elements: this.websiteElements,
      json: this.currentJsonData,
      templateData: this.templateData,
      website: this.website,
      pages: this.pages,
      heroPanelData: this.heroPanelData,
      infoPanelData: this.infoPanelData,
      bookingData: this.bookingData,
      contactData: this.contactData,
      testimonialsData: this.testimonialsData,
      footerData: this.footerData,
      headerData: this.headerData,
      currentInfoSectionKey: this.currentInfoSectionKey,
      showHeroPanel: this.showHeroPanel,
      showInfoPanel: this.showInfoPanel,
      showTestimonialsPanel: this.showTestimonialsPanel,
      showBookingPanel: this.showBookingPanel,
      showContactPanel: this.showContactPanel,
      showFooterPanel: this.showFooterPanel,
      showHeaderPanel: this.showHeaderPanel,
      showWashPackagesPanel: this.showWashPackagesPanel,
      showExpressDetailsPanel: this.showExpressDetailsPanel,
      showCouponsPanel: this.showCouponsPanel,
      showFleetsPanel: this.showFleetsPanel,
      loading: this.loading,
      closeAllPanels: () => this.closeAllPanels(),
      loadWashPackagesData: () => this.loadWashPackagesData(),
      loadExpressDetailsData: () => this.loadExpressDetailsData(),
      loadCouponsData: () => this.loadCouponsData(),
      loadFleetsData: () => this.loadFleetsData(),
    };
  }

  /** Apply element-context mutations back to the component. */
  private applyElementContext(ctx: ElementContext): void {
    this.websiteElements = ctx.elements;
    this.currentInfoSectionKey = ctx.currentInfoSectionKey;
    this.heroPanelData = ctx.heroPanelData;
    this.infoPanelData = ctx.infoPanelData;
    this.bookingData = ctx.bookingData;
    this.contactData = ctx.contactData;
    this.testimonialsData = ctx.testimonialsData;
    this.headerData = ctx.headerData;
    this.footerData = ctx.footerData;
    this.showHeroPanel = ctx.showHeroPanel;
    this.showInfoPanel = ctx.showInfoPanel;
    this.showTestimonialsPanel = ctx.showTestimonialsPanel;
    this.showBookingPanel = ctx.showBookingPanel;
    this.showContactPanel = ctx.showContactPanel;
    this.showFooterPanel = ctx.showFooterPanel;
    this.showHeaderPanel = ctx.showHeaderPanel;
    this.showWashPackagesPanel = ctx.showWashPackagesPanel;
    this.showExpressDetailsPanel = ctx.showExpressDetailsPanel;
    this.showCouponsPanel = ctx.showCouponsPanel;
    this.showFleetsPanel = ctx.showFleetsPanel;
  }

  ngOnInit(): void {
    // Ensure all panels are closed by default
    this.closeAllPanels();

    // Ensure no elements are selected by default
    if (this.websiteElements.length > 0) {
      this.websiteElements.forEach(e => (e.selected = false));
    }

    // Apply any state patches emitted by the autosave service after successful saves
    this.undoRedoSubscriptions.push(
      this.autosaveService.stateUpdate$.subscribe(update => {
        if (update.website !== undefined) this.website = update.website;
        if (update.currentJsonDataWebsite !== undefined) {
          if (!this.currentJsonData) this.currentJsonData = {};
          if (!this.currentJsonData.website) this.currentJsonData.website = {};
          Object.assign(this.currentJsonData.website, update.currentJsonDataWebsite);
        }
      })
    );

    // Subscribe to undo/redo state changes
    this.undoRedoSubscriptions.push(
      this.undoRedoService.canUndo$.subscribe(canUndo => {
        this.canUndo = canUndo;
      })
    );

    this.undoRedoSubscriptions.push(
      this.undoRedoService.canRedo$.subscribe(canRedo => {
        this.canRedo = canRedo;
      })
    );

    // Get IDs from URL parameters
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      this.templateId = params['templateId'];
      this.websiteId = params['websiteId'] || null;
      if (!this.websiteId) {
        try {
          this.websiteId = sessionStorage.getItem('websiteId');
        } catch {
          /* ignore */
        }
      }
      // If websiteId is present (with or without templateId), fetch website by id and populate from its JSON
      if (this.websiteId) {
        this.openedFromExisting = true;
        this.isPublishStatusLoading = true;
        this.loadWebsiteById(this.websiteId);
        return;
      }
      // Otherwise fall back to template-based population
      if (this.templateId) {
        this.currentTemplateId = this.templateId;
        this.loadTemplateData();
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }

    // Clean up undo/redo subscriptions
    this.undoRedoSubscriptions.forEach(sub => sub.unsubscribe());
    this.undoRedoSubscriptions = [];

    // Clear any pending autosave debounce (service-owned buffer + timer)
    this.autosaveService.destroy();
    // Legacy guard — field still kept until Phase 2 fully removes it
    if (this.autosaveDebounce) {
      clearTimeout(this.autosaveDebounce);
      this.autosaveDebounce = null;
    }

    // Clear caches
    this._sectionVisibilityMap = {};
    this._sectionVisibilityMapCacheKey = '';
    this._pagesForNavigation = [];
    this._pagesForNavigationCacheKey = '';
  }

  loadTemplateData(): void {
    const ctx = this.getDataContext();
    this.builderData.loadTemplateData(ctx, () => this.applyDataContext(ctx));
    this.applyDataContext(ctx);
  }

  private loadWebsiteById(id: string): void {
    const ctx = this.getDataContext();
    this.builderData.loadWebsiteById(id, ctx, () => this.applyDataContext(ctx));
    this.applyDataContext(ctx);
  }

  /**
   * Load pages from website API response into the pages array for sidebar display
   */
  private loadPagesFromWebsite(websitePages: any[]): void {
    const ctx = this.getDataContext();
    this.builderData.loadPagesFromWebsite(websitePages, ctx);
    this.applyDataContext(ctx);
  }

  /**
   * Load pages from template API response into the pages array for sidebar display
   */
  private loadPagesFromTemplate(templates: any[]): void {
    const ctx = this.getDataContext();
    this.builderData.loadPagesFromTemplate(templates, ctx);
    this.applyDataContext(ctx);
  }

  private populateHeroFromTemplate(): void {
    this.heroPanelData = this.templatePopulator.populateHero(
      this.currentJsonData,
      this.templateData,
      this.heroPanelData,
      this.websiteElements
    );
  }

  private extractLayoutFromContent(
    content: any
  ):
    | 'layout_1'
    | 'layout_2'
    | 'layout_3'
    | 'layout_4'
    | 'layout_5'
    | 'layout_6'
    | 'layout_7'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | undefined {
    return this.templatePopulator.extractLayoutFromContent(content);
  }

  private convertLayoutForHeroPanel(
    layoutValue: any
  ):
    | 'layout_1'
    | 'layout_2'
    | 'layout_3'
    | 'layout_4'
    | 'layout_5'
    | 'layout_6'
    | 'layout_7'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7' {
    return this.templatePopulator.convertLayoutForHeroPanel(layoutValue);
  }

  private normalizeSectionKey(sectionKey: string): string {
    return this.templatePopulator.normalizeSectionKey(sectionKey);
  }

  private populateSectionsFromTemplate(): void {
    this.templatePopulator.populateSections(
      this.currentJsonData,
      this.templateData,
      this.websiteElements
    );
  }

  /** @internal old body — here only so subsequent StrReplace can anchor on the next method */
  private populateTestimonialsFromTemplate(): void {
    this.templatePopulator.populateTestimonials(this.templateData, this.websiteElements);
  }

  private getDefaultFormFields(): any[] {
    return this.templatePopulator.getDefaultFormFields();
  }

  private populateBookingFromTemplate(): void {
    this.templatePopulator.populateBooking(
      this.currentJsonData,
      this.templateData,
      this.websiteElements
    );
  }

  private populateContactFromTemplate(): void {
    this.websiteElements = this.templatePopulator.populateContact(
      this.templateData,
      this.websiteElements
    );
  }

  private extractPageTypeFromLink(link: string): string {
    return this.templatePopulator.extractPageTypeFromLink(link);
  }

  private populateHeaderFromTemplate(): void {
    this.websiteElements = this.templatePopulator.populateHeader(
      this.currentJsonData,
      this.templateData,
      this.website,
      this.brandingData,
      this.pages,
      this.currentPageType,
      this.websiteElements
    );
  }

  private populateFooterFromTemplate(): void {
    const result = this.templatePopulator.populateFooter(
      this.currentJsonData,
      this.templateData,
      this.website,
      this.brandingData,
      this.pages,
      this.websiteElements
    );
    this.footerData = result.footerData;
    this.websiteElements = result.websiteElements;
  }

  private shouldRegenerateNavigation(currentNav: any[]): boolean {
    return this.templatePopulator.shouldRegenerateNavigation(currentNav, this.pages);
  }

  private populateFromJson(json: any): void {
    this.heroPanelData = this.templatePopulator.populateFromJson(
      json,
      this.websiteElements,
      this.heroPanelData
    );
  }

  private buildElementsFromJsonSections(): void {
    this.websiteElements = this.templatePopulator.buildElementsFromJsonSections(
      this.currentJsonData,
      this.templateData
    );
  }

  private ensureBaseElements(): void {
    this.websiteElements = this.templatePopulator.ensureBaseElements(this.websiteElements);
  }

  // Helper method to get hero data from template (for debugging)
  getHeroDataFromTemplate(): any {
    return this.templateData?.json_data?.hero || null;
  }

  onDeviceChange(device: DeviceType): void {
    this.currentDevice = device;
    this.updateDeviceDimensions(device);
  }

  private updateDeviceDimensions(device: DeviceType): void {
    const dimensions = {
      desktop: '1440x1920',
      tablet: '768x1024',
      mobile: '375x812',
    };
    this.websiteData.dimensions = dimensions[device];
  }

  onSidebarItemClick(item: 'pages' | 'branding' | 'templates'): void {
    this.activeSidebarItem = item;
    this.togglePanel(item);
  }

  private togglePanel(item: 'pages' | 'branding' | 'templates'): void {
    // Get the current state of the panel we're about to toggle
    let currentPanelState = false;
    switch (item) {
      case 'pages':
        currentPanelState = this.showPagesPanel;
        break;
      case 'branding':
        currentPanelState = this.showBrandingPanel;
        break;
      case 'templates':
        currentPanelState = this.showTemplatesPanel;
        break;
    }

    // Close all panels first (including right panels)
    this.closeAllPanels();

    // Toggle the selected panel only if it wasn't already open
    // This ensures clicking the same panel closes it, otherwise it opens
    if (!currentPanelState) {
      switch (item) {
        case 'pages':
          this.showPagesPanel = true;
          break;
        case 'branding':
          this.showBrandingPanel = true;
          break;
        case 'templates':
          this.showTemplatesPanel = true;
          // Load templates when panel is opened
          if (this.templates.length === 0) {
            this.loadTemplates();
          }
          break;
      }
    }
  }

  // Panel close methods - consolidated for reusability
  closePagesPanel(): void {
    this.showPagesPanel = false;
  }

  closeBrandingPanel(): void {
    this.showBrandingPanel = false;
  }

  closeTemplatesPanel(): void {
    this.showTemplatesPanel = false;
  }

  // Load templates from API
  loadTemplates(): void {
    this.templatesLoading = true;
    this.templatesError = null;

    this.templateService.getTemplates().subscribe({
      next: response => {
        if (response.status && response.data) {
          // Transform API data to match TemplateCardComponent interface
          // Sort by order_number if available, otherwise maintain original order
          const sortedData = Array.isArray(response.data)
            ? [...response.data].sort((a: any, b: any) => {
                const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
              })
            : response.data;

          this.templates = sortedData.map((apiTemplate: any) => ({
            id: apiTemplate.id,
            name: apiTemplate.name,
            pages: apiTemplate.pages || 5, // Default pages if not provided
            image: apiTemplate.thumbnail_url, // TemplateCardComponent expects 'image'
            selected: false,
          }));

          // Mark template as selected if it matches the current website's template_id
          if (this.templateId) {
            this.updateTemplateSelection(this.templateId);
          }

          this.logger.debug('Templates loaded:', this.templates);
        } else {
          this.templatesError = response.message || 'Failed to load templates';
        }
        this.templatesLoading = false;
      },
      error: err => {
        this.templatesError = 'Failed to load templates. Please try again.';
        this.templatesLoading = false;
        this.logger.error('Error loading templates:', err);
      },
    });
  }

  // Update template selection based on template_id
  private updateTemplateSelection(templateId: string): void {
    if (!templateId || !this.templates.length) return;

    // Mark matching template as selected
    this.templates.forEach(template => {
      template.selected = template.id === templateId;
    });

    this.logger.debug('Template selection updated. Selected template:', templateId);
  }

  closeAllPanels(): void {
    this.showPagesPanel = false;
    this.showBrandingPanel = false;
    this.showTemplatesPanel = false;
    this.showHeroPanel = false;
    this.showInfoPanel = false;
    this.showTestimonialsPanel = false;
    this.showBookingPanel = false;
    this.showContactPanel = false;
    this.showHeaderPanel = false;
    this.showFooterPanel = false;
    this.showWashPackagesPanel = false;
    this.showExpressDetailsPanel = false;
    this.showCouponsPanel = false;
    this.showFleetsPanel = false;
  }

  // Branding methods - consolidated for reusability
  onBrandingChange(brandingData: BrandingData): void {
    this.brandingData = brandingData;

    // Record state for undo/redo (debounced)
    this.recordStateChange('Branding updated');
  }

  onSetUpLater(): void {
    this.showBrandingPanel = false;
  }

  // Handle instant updates for logo and colors (immediate API call and preview update)
  onInstantBrandingUpdate(update: { type: 'logo' | 'color'; data: Partial<BrandingData> }): void {
    const ctx = this.getBrandingContext();
    this.brandingManager.onInstantBrandingUpdate(update, ctx);
    this.applyBrandingContext(ctx);
  }

  // Helper method to update branding in API (colors and/or fonts, not logo)
  private updateBrandingInAPI(brandingData: BrandingData): void {
    const ctx = this.getBrandingContext();
    this.brandingManager.updateBrandingInAPI(brandingData, ctx);
    this.applyBrandingContext(ctx);
  }

  onApplyBranding(brandingData: BrandingData): void {
    const ctx = this.getBrandingContext();
    this.brandingManager.onApplyBranding(brandingData, ctx);
    this.applyBrandingContext(ctx);
  }

  private updatePageWithBrandLogo(logoUrl: string, brandingData: BrandingData): void {
    const ctx = this.getBrandingContext();
    this.brandingManager.updatePageWithBrandLogo(logoUrl, brandingData, ctx);
    this.applyBrandingContext(ctx);
  }

  setActiveTab(tab: 'pages' | 'sections'): void {
    this.activeTab = tab;
  }

  // Toggle methods - consolidated for reusability
  onPageToggleChange(event: { index: number; item: ToggleCardData }): void {
    const ctx = this.getDataContext();
    this.builderData.onPageToggleChange(event, ctx);
    this.applyDataContext(ctx);
    this.syncFooterQuickLinksWithPages();
  }

  /** Keep footer Quick links aligned with header page visibility and persist them. */
  private syncFooterQuickLinksWithPages(): void {
    const result = this.templatePopulator.populateFooter(
      this.currentJsonData,
      this.templateData,
      this.website,
      this.brandingData,
      this.pages,
      this.websiteElements
    );
    this.footerData = result.footerData;
    this.websiteElements = result.websiteElements;

    const infoCtx = this.getInfoContext();
    this.infoContentService.persistFooter(this.footerData, infoCtx);
    this.applyInfoContext(infoCtx);
  }

  /**
   * Handle page click to switch between pages
   * Called when user clicks on a page card in the sidebar
   */
  /**
   * Handle navigation click from preview components (header buttons, navigation links)
   */
  onNavigationClick(pageType: string): void {
    const ctx = this.getDataContext();
    this.builderData.onNavigationClick(pageType, ctx);
    this.applyDataContext(ctx);
  }

  /**
   * Load page data - common method used by both onPageClick and initialization
   */
  private loadPageData(selectedPage: any, pageIndex: number): void {
    const ctx = this.getDataContext();
    this.builderData.loadPageData(selectedPage, pageIndex, ctx);
    this.applyDataContext(ctx);
  }

  onPageClick(event: { index: number; item: ToggleCardData }): void {
    const ctx = this.getDataContext();
    this.builderData.onPageClick(event, ctx);
    this.applyDataContext(ctx);
  }

  /**
   * Load sections from current page's JSON data
   * Dynamically extracts sections based on page type
   */
  private loadSectionsFromPage(): void {
    const ctx = this.getDataContext();
    this.builderData.loadSectionsFromPage(ctx);
    this.applyDataContext(ctx);
  }

  /**
   * Format section key to display name
   */
  private formatSectionName(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Map section names to element IDs for visibility control
   * Works for all page types (Home, Express Wash Packages, Express Auto Details, Fleet, Coupons)
   */
  getSectionVisibilityMap(): Record<string, boolean> {
    const ctx = this.getDataContext();
    return this.builderData.getSectionVisibilityMap(ctx);
  }

  /**
   * Derive section key from display name
   */
  private deriveSectionKey(displayName: string): string | null {
    // Convert display names like "Package Section" to "package_section"
    const key = displayName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');

    return key || null;
  }

  onSectionToggleChange(event: { index: number; item: ToggleCardData }): void {
    const ctx = this.getDataContext();
    this.builderData.onSectionToggleChange(event, ctx);
    this.applyDataContext(ctx);
  }

  /**
   * Save section visibility changes to API
   */
  private saveSectionVisibility(): void {
    if (!this.websiteId || !this.websitePageId) {
      this.logger.warn('Missing websiteId or websitePageId; cannot save section visibility.');
      return;
    }

    if (!this.currentJsonData) {
      this.logger.warn('No current JSON data; cannot save section visibility.');
      return;
    }

    // Clean up old structure before saving
    const cleanedJsonData = this.cleanupJsonStructure(this.currentJsonData);

    // Generate HTML data from updated JSON
    const htmlData = this.generateHtmlFromJson(cleanedJsonData);

    // Update the website page via API
    this.templateService
      .updateWebsitePage(this.websitePageId, cleanedJsonData, htmlData, this.websiteId)
      .subscribe({
        next: () => {
          this.logger.debug('✅ Section visibility updated successfully');
          // Update editableJson to reflect changes
          try {
            // Also update currentJsonData to the cleaned version
            this.currentJsonData = cleanedJsonData;
          } catch (e) {
            this.logger.error('Error stringifying JSON:', e);
          }
        },
        error: err => {
          this.logger.error('❌ Failed to update section visibility:', err);
        },
      });
  }

  // Action methods - cleaned up for reusability
  onLogoutClick(): void {
    window.close();
  }

  onSettingsClick(): void {
    this.initialSettingsSidebarItem = null; // Reset to default (general)
    this.showSettingsPage = true;
    this.closeAllPanels();
  }

  onSettingsClose(): void {
    this.showSettingsPage = false;
    this.initialSettingsSidebarItem = null; // Reset when closing
    this.refreshWebsitePublishStatus();
  }

  onSettingsSave(settings: any): void {
    this.logger.debug('Settings saved:', settings);
    // Update the website data with new settings
    this.websiteData = {
      ...this.websiteData,
      ...settings,
    };
    // Call API to save settings
    // this.showSuccessToast('Settings saved successfully');
  }

  onDomainConfirmed(): void {
    if (!this.websiteId || this.isPublishConfigured || this.isPublishToggling) {
      this.refreshWebsitePublishStatus();
      return;
    }

    this.isPublishToggling = true;
    this.templateService.publishWebsite(this.websiteId).subscribe({
      next: () => {
        this.isPublishToggling = false;
        this.setPublishState(true, false);
        this.refreshWebsitePublishStatus();
      },
      error: err => {
        this.isPublishToggling = false;
        this.logger.error('Failed to publish website after domain setup:', err);
      },
    });
  }

  onWebsiteUnpublish(): void {
    if (!this.websiteId || this.isPublishToggling) {
      return;
    }

    this.isPublishToggling = true;
    this.templateService.unpublishWebsite(this.websiteId).subscribe({
      next: () => {
        this.isPublishToggling = false;
        this.showSettingsPage = false;
        this.setPublishState(false, true);
        this.refreshWebsitePublishStatus();
      },
      error: err => {
        this.isPublishToggling = false;
        this.logger.error('Failed to unpublish website:', err);
      },
    });
  }

  onPreviewClick(): void {
    // Get the current page type from the selected page
    const selectedPage = this.pages.find((p: any) => p.selected);
    const pageType = selectedPage ? (selectedPage as any).pageType : this.currentPageType || 'home';

    // If the website is published, open the live URL
    const websiteFields = this.website?.json_data?.website || this.website;
    if (
      this.isWebsitePublished &&
      (websiteFields?.subdomain || websiteFields?.custom_domain || websiteFields?.domain)
    ) {
      const liveUrl = getBaseWebsiteUrl(this.website);
      if (liveUrl) {
        const path = pageType === 'home' ? '' : `/${pageType}`;
        window.open(`${liveUrl}${path}`, '_blank');
        return;
      }
    }

    // Open preview with websiteId if available, including the current page type
    if (this.websiteId) {
      window.open(`/preview?websiteId=${this.websiteId}&pageType=${pageType}`, '_blank');
    } else if (this.templateId) {
      window.open(`/preview?id=${this.templateId}&pageType=${pageType}`, '_blank');
    } else {
      window.open(`/preview?pageType=${pageType}`, '_blank');
    }
  }

  get isPublishButtonLoading(): boolean {
    return this.isPublishStatusLoading || this.isPublishToggling;
  }

  get isWebsitePublished(): boolean {
    return this.websitePublishStatus;
  }

  get isPublishConfigured(): boolean {
    return this.websitePublishStatus || this.websiteUnpublishStatus;
  }

  get publishButtonLabel(): string {
    return this.isWebsitePublished ? 'Unpublish' : 'Publish';
  }

  get publishButtonTooltip(): string {
    if (this.isPublishButtonLoading) {
      return 'Loading...';
    }
    return this.isWebsitePublished ? 'Unpublish Website' : 'Publish Website';
  }

  get websiteStatusLabel(): string {
    if (this.isPublishStatusLoading) {
      return 'Draft';
    }
    return this.isWebsitePublished ? 'Published' : 'Draft';
  }

  onPublishClick(): void {
    if (this.isPublishButtonLoading) {
      return;
    }

    if (!this.isPublishConfigured) {
      this.initialSettingsSidebarItem = 'domains';
      this.showSettingsPage = true;
      this.closeAllPanels();
      return;
    }

    if (!this.websiteId || this.isPublishToggling) {
      return;
    }

    const wasPublished = this.isWebsitePublished;
    const action$ = wasPublished
      ? this.templateService.unpublishWebsite(this.websiteId)
      : this.templateService.publishWebsite(this.websiteId);

    this.isPublishToggling = true;
    action$.subscribe({
      next: () => {
        this.isPublishToggling = false;
        this.setPublishState(!wasPublished, wasPublished);
        this.refreshWebsitePublishStatus();
      },
      error: err => {
        this.isPublishToggling = false;
        this.logger.error('Failed to toggle publish status:', err);
      },
    });
  }

  private refreshWebsitePublishStatus(): void {
    if (!this.websiteId) {
      return;
    }

    this.templateService
      .getWebsiteById(this.websiteId)
      .pipe(
        finalize(() => {
          this.isPublishStatusLoading = false;
        })
      )
      .subscribe({
        next: resp => {
          const data = resp?.data;
          if (!data) {
            return;
          }

          const websiteBase = data.website || data;
          const publish = data.publish ?? websiteBase.publish ?? false;
          const unpublish = data.unpublish ?? websiteBase.unpublish ?? false;

          // Ensure this.website has the latest properties (like subdomain/domain)
          this.website = this.website ? { ...this.website, ...websiteBase } : { ...websiteBase };

          this.setPublishState(!!publish, !!unpublish);
        },
        error: err => this.logger.error('Failed to refresh publish status:', err),
      });
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (this.document.visibilityState === 'visible' && this.websiteId) {
      this.refreshWebsitePublishStatus();
    }
  }

  onToolbarAction(action: ToolbarAction): void {
    this.executeAction(action.id);
  }

  private executeAction(actionId: string): void {
    switch (actionId) {
      case 'undo':
        this.handleUndo();
        break;
      case 'redo':
        this.handleRedo();
        break;
      case 'refresh':
        this.handleRefresh();
        break;
      case 'maximize':
        this.handleMaximize();
        break;
      case 'zoom-in':
        this.handleZoomIn();
        break;
      case 'zoom-out':
        this.handleZoomOut();
        break;
    }
  }

  private handleUndo(): void {
    const previousState = this.undoRedoService.undo();
    if (previousState) {
      this.restoreState(previousState);
      this.logger.debug('↩️ Undo completed');
    } else {
      this.logger.warn('⚠️ Cannot undo - no history available');
    }
  }

  private handleRedo(): void {
    const nextState = this.undoRedoService.redo();
    if (nextState) {
      this.restoreState(nextState);
      this.logger.debug('↪️ Redo completed');
    } else {
      this.logger.warn('⚠️ Cannot redo - no redo history available');
    }
  }

  private handleRefresh(): void {
    // Reload the website preview
    window.location.reload();
  }

  private handleMaximize(): void {
    // Reset zoom to original/default level (40%)
    this.zoomLevel = this.DEFAULT_ZOOM_LEVEL;
  }

  private handleZoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 10, 200);
  }

  private handleZoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 10, 10); // Allow zooming out to 10% minimum
  }

  getBaseWidth(): number {
    // Base widths for different devices (at 100% scale)
    // The transform scale will handle the zoom, so we keep the base width constant
    const baseWidths = {
      desktop: 1440,
      tablet: 768,
      mobile: 375,
    };

    return baseWidths[this.currentDevice] || baseWidths.desktop;
  }

  onTemplatePreview(template: any): void {
    this.logger.debug('Template preview:', template);
    // Open preview in new tab
    window.open(`/preview?id=${template.id}`, '_blank');
  }

  onTemplateSelect(template: any): void {
    this.logger.debug('Template selected:', template);

    // Check if the selected template is different from the current one
    if (this.templateId && template.id !== this.templateId) {
      // Show confirmation modal - don't update selection state yet
      this.pendingTemplate = template;
      this.showReplaceTemplateModal = true;
    } else {
      // Same template or no current template, just update selection state
      this.templates.forEach(t => (t.selected = t.id === template.id));
      this.logger.debug('Template selection updated (same template or no current template)');
    }
  }

  onReplaceTemplateConfirm(): void {
    if (!this.pendingTemplate || !this.websiteId) {
      this.logger.error('Cannot replace template: missing template or website ID');
      this.showReplaceTemplateModal = false;
      this.pendingTemplate = null;
      return;
    }

    this.isReplacingTemplate = true;
    const template = this.pendingTemplate;

    // First, reset the website
    this.logger.debug('Resetting website:', this.websiteId);
    this.templateService.resetWebsite(this.websiteId).subscribe({
      next: resetResponse => {
        this.logger.debug('Website reset successfully:', resetResponse);

        // Then, create/select the new template (this is the template select API)
        this.logger.debug('Creating website with new template:', template);
        const request = { name: template.name, template_id: template.id };
        this.templateService.createWebsite(request).subscribe({
          next: createResponse => {
            this.logger.debug('Website created with new template successfully:', createResponse);
            const data: any = (createResponse as any)?.data || {};
            const newWebsiteId = data.website_id || data.id || this.websiteId;
            const newTemplateId = data.template_id || template.id;

            // Update selection state before reloading
            this.templates.forEach(t => (t.selected = t.id === template.id));

            // Reload the website builder page with new IDs
            const url = `/website-builder?templateId=${newTemplateId}${newWebsiteId ? `&websiteId=${newWebsiteId}` : ''}`;
            window.location.href = url;
          },
          error: error => {
            this.logger.error('Error creating website with new template:', error);
            this.isReplacingTemplate = false;
            this.showReplaceTemplateModal = false;
            this.pendingTemplate = null;
          },
        });
      },
      error: error => {
        this.logger.error('Error resetting website:', error);
        this.isReplacingTemplate = false;
        this.showReplaceTemplateModal = false;
        this.pendingTemplate = null;
      },
    });
  }

  onReplaceTemplateCancel(): void {
    this.showReplaceTemplateModal = false;
    this.pendingTemplate = null;
  }

  onWebsiteElementSelect(element: any): void {
    const ctx = this.getElementContext();
    this.elementSelection.onWebsiteElementSelect(element, ctx);
    this.applyElementContext(ctx);
  }

  private updateElementSelection(selectedElement: any): void {
    const ctx = this.getElementContext();
    this.elementSelection.updateElementSelection(selectedElement, ctx);
    this.applyElementContext(ctx);
  }

  private handleElementSelection(element: any): void {
    const ctx = this.getElementContext();
    this.elementSelection.handleElementSelection(element, ctx);
    this.applyElementContext(ctx);
  }

  private setEditorDataFromElement(element: any): void {
    const ctx = this.getElementContext();
    this.elementSelection.setEditorDataFromElement(element, ctx);
    this.applyElementContext(ctx);
  }

  // Hero Panel Methods
  closeHeroPanel(): void {
    const ctx = this.getHeroContext();
    this.heroPanelService.closeHeroPanel(ctx);
    this.applyHeroContext(ctx);
  }

  // Info Panel Methods
  closeInfoPanel(): void {
    const ctx = this.getInfoContext();
    this.infoContentService.closeInfoPanel(ctx);
    this.applyInfoContext(ctx);
  }

  // Wash Packages Panel Methods

  // --- Wash Packages Panel ---

  closeWashPackagesPanel(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.closeWashPackagesPanel(ctx);
    this.applySectionContext(ctx);
  }

  loadWashPackagesData(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.loadWashPackagesData(ctx);
    this.applySectionContext(ctx);
  }

  onWashPackagesChange(data: WashPackagePanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onWashPackagesChange(data, ctx);
    this.applySectionContext(ctx);
  }

  onWashPackagesSave(data: WashPackagePanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onWashPackagesSave(data, ctx);
    this.applySectionContext(ctx);
  }

  // --- Express Details Panel ---

  closeExpressDetailsPanel(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.closeExpressDetailsPanel(ctx);
    this.applySectionContext(ctx);
  }

  loadExpressDetailsData(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.loadExpressDetailsData(ctx);
    this.applySectionContext(ctx);
  }

  onExpressDetailsChange(data: ExpressDetailsPanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onExpressDetailsChange(data, ctx);
    this.applySectionContext(ctx);
  }

  onExpressDetailsSave(data: ExpressDetailsPanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onExpressDetailsSave(data, ctx);
    this.applySectionContext(ctx);
  }

  // --- Coupons Panel ---

  closeCouponsPanel(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.closeCouponsPanel(ctx);
    this.applySectionContext(ctx);
  }

  loadCouponsData(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.loadCouponsData(ctx);
    this.applySectionContext(ctx);
  }

  onCouponsChange(data: CouponsPanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onCouponsChange(data, ctx);
    this.applySectionContext(ctx);
  }

  onCouponsSave(data: CouponsPanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onCouponsSave(data, ctx);
    this.applySectionContext(ctx);
  }

  // --- Fleets Panel ---

  closeFleetsPanel(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.closeFleetsPanel(ctx);
    this.applySectionContext(ctx);
  }

  loadFleetsData(): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.loadFleetsData(ctx);
    this.applySectionContext(ctx);
  }

  onFleetsChange(data: FleetsPanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onFleetsChange(data, ctx);
    this.applySectionContext(ctx);
  }

  onFleetsSave(data: FleetsPanelData): void {
    const ctx = this.getSectionContext();
    this.sectionPanels.onFleetsSave(data, ctx);
    this.applySectionContext(ctx);
  }

  onInfoPanelChange(data: InfoPanelData): void {
    const ctx = this.getInfoContext();
    this.infoContentService.onInfoPanelChange(data, ctx);
    this.applyInfoContext(ctx);
  }

  onInfoPanelSave(data: InfoPanelData): void {
    const ctx = this.getInfoContext();
    this.infoContentService.onInfoPanelSave(data, ctx);
    this.applyInfoContext(ctx);
  }

  // Testimonials live change
  onTestimonialsChange(data: TestimonialsData): void {
    const ctx = this.getInfoContext();
    this.infoContentService.onTestimonialsChange(data, ctx);
    this.applyInfoContext(ctx);
  }

  // Booking live change
  onBookingChange(data: BookingData): void {
    const ctx = this.getInfoContext();
    this.infoContentService.onBookingChange(data, ctx);
    this.applyInfoContext(ctx);
  }

  // Helper method to get full address string from contact data
  private getFullAddressFromContactData(data: ContactData): string {
    if (typeof data.contactInfo?.address === 'string') {
      return data.contactInfo.address;
    }
    if (data.contactInfo?.address) {
      const addr = data.contactInfo.address;
      const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter(p => p);
      return parts.join(', ');
    }
    return '';
  }

  // Footer change handler
  onFooterChange(data: FooterData): void {
    const ctx = this.getInfoContext();
    this.infoContentService.onFooterChange(data, ctx);
    this.applyInfoContext(ctx);
  }

  // Contact live change
  onHeaderChange(data: HeaderData): void {
    const ctx = this.getInfoContext();
    this.infoContentService.onHeaderChange(data, ctx);
    this.applyInfoContext(ctx);
  }

  getPagesForNavigation(): any[] {
    // Filter out inactive pages first
    const activePages = this.pages.filter((p: any) => p.visible !== false);

    // Create cache key from all pages (including visibility status) to detect changes
    const cacheKey = this.pages.map((p: any) => `${p.name}:${p.pageType}:${p.visible}`).join('|');

    // Return cached result if pages haven't changed
    if (cacheKey === this._pagesForNavigationCacheKey && this._pagesForNavigation.length > 0) {
      return this._pagesForNavigation;
    }

    // Recompute pages for navigation (only active pages)
    const pagesForNav = activePages.map((page: any) => ({
      name: page.name,
      pageType: page.pageType,
      pageId: page.pageId,
    }));

    // Cache the result
    this._pagesForNavigation = pagesForNav;
    this._pagesForNavigationCacheKey = cacheKey;

    return pagesForNav;
  }

  onContactChange(data: ContactData): void {
    const ctx = this.getInfoContext();
    this.infoContentService.onContactChange(data, ctx);
    this.applyInfoContext(ctx);
  }

  onHeroPanelSave(data: HeroPanelData): void {
    const ctx = this.getHeroContext();
    this.heroPanelService.onHeroPanelSave(data, ctx);
    this.applyHeroContext(ctx);
  }

  // Live change handler to autosave while typing (debounced/throttled)
  onHeroPanelChange(data: HeroPanelData): void {
    const ctx = this.getHeroContext();
    this.heroPanelService.onHeroPanelChange(data, ctx);
    this.applyHeroContext(ctx);
  }

  // Helper method to generate HTML from JSON (simplified version)
  private generateHtmlFromJson(jsonData: any): string {
    return this.autosaveService.generateHtmlFromJson(jsonData);
  }

  private scheduleAutosave(paths: string[]): void {
    this.autosaveService.schedule(
      paths,
      this.currentJsonData,
      this.getContext(),
      undefined,
      this.website
    );
  }

  private flushAutosave(): void {
    this.autosaveService.flush(this.currentJsonData, this.getContext(), this.website);
  }

  private cleanupJsonStructure(jsonData: any): any {
    return this.autosaveService.cleanupJsonStructure(jsonData);
  }

  private getJsonAtPath(obj: any, pointer: string): any {
    return this.autosaveService.getJsonAtPath(obj, pointer);
  }

  /** Capture current state as a snapshot for undo/redo. */
  private captureCurrentState(description?: string): StateSnapshot {
    return {
      jsonData: this.currentJsonData ? JSON.parse(JSON.stringify(this.currentJsonData)) : null,
      websiteElements: JSON.parse(JSON.stringify(this.websiteElements)),
      heroPanelData: JSON.parse(JSON.stringify(this.heroPanelData)),
      infoPanelData: JSON.parse(JSON.stringify(this.infoPanelData)),
      testimonialsData: JSON.parse(JSON.stringify(this.testimonialsData)),
      bookingData: JSON.parse(JSON.stringify(this.bookingData)),
      contactData: JSON.parse(JSON.stringify(this.contactData)),
      headerData: JSON.parse(JSON.stringify(this.headerData)),
      footerData: JSON.parse(JSON.stringify(this.footerData)),
      brandingData: JSON.parse(JSON.stringify(this.brandingData)),
      timestamp: Date.now(),
      description: description,
      currentPageType: this.currentPageType,
      currentInfoSectionKey: this.currentInfoSectionKey,
    };
  }

  /** Restore component state from a previously captured snapshot. */
  private restoreState(snapshot: StateSnapshot): void {
    this.logger.debug('🔄 Restoring state from snapshot:', snapshot.description || 'unnamed');

    if (snapshot.jsonData) this.currentJsonData = JSON.parse(JSON.stringify(snapshot.jsonData));
    if (snapshot.websiteElements)
      this.websiteElements = JSON.parse(JSON.stringify(snapshot.websiteElements));
    if (snapshot.heroPanelData)
      this.heroPanelData = JSON.parse(JSON.stringify(snapshot.heroPanelData));
    if (snapshot.infoPanelData)
      this.infoPanelData = JSON.parse(JSON.stringify(snapshot.infoPanelData));
    if (snapshot.testimonialsData)
      this.testimonialsData = JSON.parse(JSON.stringify(snapshot.testimonialsData));
    if (snapshot.bookingData) this.bookingData = JSON.parse(JSON.stringify(snapshot.bookingData));
    if (snapshot.contactData) this.contactData = JSON.parse(JSON.stringify(snapshot.contactData));
    if (snapshot.headerData) this.headerData = JSON.parse(JSON.stringify(snapshot.headerData));
    if (snapshot.footerData) this.footerData = JSON.parse(JSON.stringify(snapshot.footerData));

    if (snapshot.brandingData) {
      this.brandingData = JSON.parse(JSON.stringify(snapshot.brandingData));
      if (this.brandingData.colors) {
        this.primaryColor = this.brandingData.colors.primary;
        this.headingColor = this.brandingData.colors.heading;
        this.paragraphColor = this.brandingData.colors.paragraph;
      }
      if (this.brandingData.fonts) {
        this.headingFont = this.brandingData.fonts.heading;
        this.bodyFont = this.brandingData.fonts.body;
      }
    }

    if (snapshot.currentPageType) this.currentPageType = snapshot.currentPageType;
    if (snapshot.currentInfoSectionKey) this.currentInfoSectionKey = snapshot.currentInfoSectionKey;

    this.scheduleAutosave(['page']);
    this.logger.debug('✅ State restored successfully');
  }

  /** Set master state for undo/redo after initial data load. */
  private initializeMasterState(description = 'Initial state from API'): void {
    const masterSnapshot = this.captureCurrentState(description);
    this.undoRedoService.setMasterState(masterSnapshot);
    this.logger.debug('🎯 Master state initialized:', description);
  }

  /** Record current state for undo/redo (debounced). */
  private recordStateChange(description?: string, immediate = false): void {
    const snapshot = this.captureCurrentState(description);
    this.undoRedoService.recordState(snapshot, immediate);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
